import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { Input, Button, Confirm, Header } from 'semantic-ui-react';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _head from 'lodash/head';
import _isEmpty from 'lodash/isEmpty';
import _isNumber from 'lodash/isNumber';
import { useDispatch, useSelector } from 'react-redux';

import { isAdvancedLocationFeaturesSupported } from 'components/common/location/Location/Location.utils';
import ServiceItemLocationSettings from 'components/modules/location/components/ServiceItemLocationSettings';
import { isAdminUser } from 'libs/auth';
import { selectBusinessCatalogCategories, selectBusinessCatalogItems } from 'selectors/catalog';
import { selectUser } from 'selectors/user';

import {
  BUTTONS_TOOLTIP_DESCRIPTION,
  SETTINGS_OPTIONS,
  ITEM_LONG_DESCRIPTION_MAX_LENGTH,
  ITEM_CATALOG_OBJECT_TYPE,
  ITEM_LONG_DESCRIPTION_MIN_LENGTH,
  ITEM_SHORT_DESCRIPTION_MAX_LENGTH,
  ITEM_SHORT_DESCRIPTION_MIN_LENGTH,
  ITEM_SHORT_DESCRIPTION_ADDITIONAL_REQUIREMENTS,
  ITEM_SHORT_DESCRIPTION_PLACEHOLDER,
  ITEM_SHORT_DESCRIPTION_TOOL_TIP,
  ITEM_LONG_DESCRIPTION_PLACEHOLDER,
  ITEM_LONG_DESCRIPTION_TOOL_TIP,
  GLOBAL_PRICE_LABEL_TOOL_TIP,
  ITEM_SEO_TITLE_PROMPT_CLASS,
  ITEM_SEO_DESCRIPTION_PROMPT_CLASS,
} from './EditMenuItem.constants';
import { isImageUrlEmpty, cleanExtraData } from './EditMenuItem.utils';
import ExtraDataGroupForm from './components/ExtraDataGroupForm/ExtraDataGroupForm';
import { updateMenuItem, deleteMenuItem, flagMenuSaved } from '../../../../../actions/business';
import API from '../../../../../libs/api';
import { sortItemsByList } from '../../../../../libs/array';
import { getErrorMessage } from '../../../../../libs/errors';
import ErrorHandler from '../../../../../libs/errors/errors';
import { objectToCamelCase, objectToSnakeCase } from '../../../../../libs/format/case';
import {
  selectBusiness,
  selectBusinessFiles,
  selectBusinessName,
  selectIsSingleLocation,
} from '../../../../../selectors/business';
import HelpTooltip from '../../../../common/HelpTooltip';
import CatalogObjectDescriptionInput from '../../../../fields/CatalogObjectDescriptionInput';
import { CatalogItemPropType } from '../../../../modules/catalog/proptypes/catalog';
import EditFileField from '../../../../modules/files/components/EditFileField';
import FileSelection from '../../../../modules/files/components/FileSelection';
import { SOURCE_TYPE_MENU } from '../../../../modules/files/constants/sources';
import { getImageUrl } from '../../../../modules/files/services/files';
import { ITEM_PAGE_TYPE } from '../../../../modules/pages/constants';
import { ImageAltTextField } from '../../../../modules/seo/components';
import SeoMetadataFields from '../../../../modules/seo/components/SeoMetadataFields';
import { createSeoTitlePlaceholder } from '../../../../modules/seo/components/SeoMetadataFields/components/SeoTitle/SeoTitle.utils';
import ProductDescriptionPageWidget from '../../../../modules/website-component-interaction/components/ProductDescriptionPageWidget';
import WebsiteComponentInteractionContainer from '../../../../modules/website-component-interaction/components/WebsiteComponentInteractionContainer';
import {
  filterClickthruInteractions,
  filterNonClickthruInteractions,
} from '../../../../modules/website-component-interaction/utils';
import { InventoryConsumer, InventoryContext } from '../../../MenuForm/Menu.context';
import EditMenuModal from '../../common/EditMenuModal';
import SettingsWidget from '../../common/SettingsWidget/SettingsWidget';
import {
  areCtasEnabledForRecipe,
  isFeaturedFeatureEnabled,
  getBusinessMenuSettings,
  isMultipleImagesSupported,
  isClickTroughUrlEnabledForBusiness,
  isDetailedDescriptionEnabled,
  isAdvancedSeoSupported,
  areAdditionalServiceAttributesSupported,
  isGlobalPriceLabelSupported,
} from '../../libs/menu-helpers';
import MenuItemModifierSets from '../../modifier-set/MenuItemModifierSets/MenuItemModifierSets';
import MenuItemVariants from '../../variant/MenuItemVariants';

import './EditMenuItem.scss';

const propTypes = {
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  categoryIndex: PropTypes.number.isRequired,

  itemIndex: PropTypes.number.isRequired,
  initialItem: CatalogItemPropType,

  onCloseModal: PropTypes.func,
  open: PropTypes.bool,
};

const defaultProps = {
  initialItem: {},
  onCloseModal: null,
  open: false,
};

export default function EditMenuItem({
  categoryId,
  categoryIndex,
  itemIndex,
  initialItem,
  onCloseModal,
  open,
}) {
  const dispatch = useDispatch();

  const business = useSelector(selectBusiness);
  const businessName = useSelector(selectBusinessName);
  const allItems = useSelector(selectBusinessCatalogItems);
  const allCategories = useSelector(selectBusinessCatalogCategories);
  const businessFiles = useSelector(selectBusinessFiles);
  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);

  const isSingleLocation = useSelector(selectIsSingleLocation);
  const isMultiLocation = !isSingleLocation;

  const { activePatch } = useContext(InventoryContext);

  const [errorMessages, setErrorMessages] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [formData, setFormData] = useState(_cloneDeep(initialItem || {}));

  function resetFormData() {
    setFormData(_cloneDeep(initialItem || {}));
    setErrorMessages([]);
  }

  const { id: businessId, type: businessType } = business;

  const category = allCategories[categoryIndex];
  const {
    id: itemId,
    localId: itemLocalId,
    name = '',

    description = '',
    fullDescription = '',
    image,
    imageAltText = '',
    interactions = [],
    files = [],

    isFeatured = false,
    visible = true,
    available = true,
    isSpecial = false,

    globalPriceLabel = '',
    variations: itemVariations = [],
    modifierSets: itemModifierSets = [],

    seoTitle = '',
    seoOgTitle = '',
    seoDescription = '',
    seoOgDescription = '',
    seoOgImage,
    extraData = {
      attributes: [],
    },
    duration,

    locations: locationSettings = [],
  } = formData || {};

  const seoOgImageId = _isNumber(seoOgImage) ? seoOgImage : _get(seoOgImage, 'id', null);

  const isNew = !itemId;

  function closeModal() {
    resetFormData();
    if (onCloseModal) {
      onCloseModal();
    }
  }

  function validateForm() {
    const response = { formIsValid: false, errors: [] };

    if (_isEmpty(name)) {
      response.errors.push('Please enter an item name.');
    }
    if (_isEmpty(itemVariations)) {
      response.errors.push('Please include at least one variation');
    } else {
      itemVariations.forEach((variation) => {
        if (!variation.name) {
          response.errors.push('Please enter a name for each variation.');
        }
      });
    }

    response.formIsValid = _isEmpty(response.errors);
    return response;
  }

  function updateMenuItemState(payload = {}) {
    setFormData((prevFormData) => ({ ...prevFormData, ...objectToCamelCase(payload) }));
  }

  async function onSaveNewItem() {
    const { formIsValid, errors } = validateForm();
    if (!formIsValid) {
      setErrorMessages(errors);
      return;
    }

    setSaveLoading(true);
    try {
      const payload = objectToSnakeCase({
        name,
        description,
        globalPriceLabel,
        variations: itemVariations,
        modifier_sets: itemModifierSets,
        order: allItems.length,
        interactions,
        isFeatured,
        fullDescription,
        files,
        seoTitle,
        seoOgTitle,
        seoDescription,
        seoOgDescription,
        seoOgImage: seoOgImageId,
        imageAltText,
        extraData: cleanExtraData(extraData),
        duration,
        locations: locationSettings,
        categoryId,
        fillDataWithAi: !!isAdmin,
      });

      const { data: createdItem } = await API.createMenuItem(businessId, businessType, payload);

      let imageData = {};
      if (!_isEmpty(image) && image.file) {
        const imagePayload = new FormData();
        imagePayload.append('image', image.file);
        const { data: imageDataResponse } = await API.updateMenuItem(
          businessId,
          businessType,
          createdItem.id,
          imagePayload,
        );
        imageData = imageDataResponse;
      }

      const newCategoryItems = [
        ...new Set([...category.items.filter((id) => id !== itemLocalId), createdItem.id]),
      ];
      const { data: categoryItem } = await API.addMenuCategoryItem(
        businessId,
        businessType,
        categoryId,
        newCategoryItems,
      );

      dispatch(
        updateMenuItem({
          index: itemIndex,
          values: { ...createdItem, ...imageData, index: itemIndex },
          categoryIndex,
          localItemId: itemLocalId,
          items: categoryItem.items,
        }),
      );

      updateMenuItemState({ ...createdItem, ...imageData, index: itemIndex });
      closeModal();

      dispatch(flagMenuSaved(true));
    } catch (e) {
      setErrorMessages(['There was an error creating the item. ', getErrorMessage(e)]);
    }

    setSaveLoading(false);
  }

  async function onSaveItem() {
    const { formIsValid, errors } = validateForm();
    if (!formIsValid) {
      setErrorMessages(errors);
      return;
    }

    setSaveLoading(true);
    try {
      const payload = objectToSnakeCase({
        name,
        description,
        globalPriceLabel,
        variations: itemVariations.map((variation) => ({
          ...variation,
          menuItem: itemId,
        })),
        modifierSets: itemModifierSets,
        visible,
        available,
        isSpecial,
        isFeatured,
        fullDescription,
        interactions,
        files,
        seoTitle,
        seoOgTitle,
        seoDescription,
        seoOgDescription,
        seoOgImage: seoOgImageId,
        imageAltText,
        extraData: cleanExtraData(extraData),
        duration,
        locations: locationSettings,
      });

      if (image && typeof image === 'object') {
        const imagePayload = new FormData();
        // make sure that if you want to remove the image, to send an empty string ''
        imagePayload.append('image', image.file || '');
        await API.updateMenuItem(businessId, businessType, itemId, imagePayload);
      }

      const { data: updatedItem } = await API.updateMenuItem(
        businessId,
        businessType,
        itemId,
        payload,
      );

      dispatch(updateMenuItem({ index: itemIndex, values: { ...updatedItem, index: itemIndex } }));
      updateMenuItemState({ ...updatedItem, index: itemIndex });

      closeModal();

      dispatch(flagMenuSaved(true));
    } catch (e) {
      ErrorHandler.capture(e);
      const errorMessage = getErrorMessage(e);
      setErrorMessages([errorMessage]);
    } finally {
      setSaveLoading(false);
    }
  }

  async function onDeleteItem() {
    setDeleteLoading(true);

    try {
      await API.deleteMenuItem(businessId, businessType, itemId);
      dispatch(deleteMenuItem({ index: itemIndex, categoryIndex, itemId }));
      closeModal();

      dispatch(flagMenuSaved(true));
    } catch (e) {
      setErrorMessages(['There was an error deleting the item.']);
    }

    setDeleteLoading(false);
  }

  function onDeleteNewItem() {
    dispatch(deleteMenuItem({ index: itemIndex, categoryIndex, itemId: itemId || itemLocalId }));
    closeModal();
  }

  function onImageDelete() {
    updateMenuItemState({ image: { url: null, file: null } });
  }

  function onImageChange(e, { files: imageFiles }) {
    const file = imageFiles[0];
    const url = window.URL.createObjectURL(file);

    const payload = { image: { url, file } };
    setFormData((prevFormData) => ({ ...prevFormData, ...payload }));
  }

  function onChangeAltText(e, { value }) {
    updateMenuItemState({ imageAltText: value });
  }

  function onVariationsChange(variations) {
    updateMenuItemState({ variations });
  }

  function handleChange(e, { name: currentName, value, checked, type }) {
    updateMenuItemState({ [currentName]: type === 'checkbox' ? checked : value });
  }

  function onUpdateLocationSettings(e, { value }) {
    const newValues = value.map((setting) => ({
      ...setting,
      location: setting.locationId,
    }));

    updateMenuItemState({ locations: newValues });
  }

  function onUpdateWebsiteComponentInteractions(firstNonClickthroughInteractions) {
    const originalNonClickthroughInteractions = filterNonClickthruInteractions(interactions);
    const nonClickthroughInteractions = [
      ...firstNonClickthroughInteractions,
      ...originalNonClickthroughInteractions.slice(firstNonClickthroughInteractions.length),
    ];
    const clickthroughInteractions = filterClickthruInteractions(interactions);

    updateMenuItemState({
      interactions: [...nonClickthroughInteractions, ...clickthroughInteractions],
    });
  }

  function onUpdateFiles(newFiles = []) {
    const fileIds = newFiles.map((file) => (_isNumber(file) ? file : file.id));
    updateMenuItemState({ files: fileIds });
  }

  function onUpdateModifierSets(newModifierSets) {
    const modifierSetIds = newModifierSets.map((modifierSet) =>
      _isNumber(modifierSet) ? modifierSet : modifierSet.id,
    );
    updateMenuItemState({ modifierSets: modifierSetIds });
  }

  function onUpdateClickthruInteractions({ values: { interactions: updatedInteractions = [] } }) {
    const nonClickthroughInteractions = filterNonClickthruInteractions(interactions);

    updateMenuItemState({
      interactions: [...nonClickthroughInteractions, ...updatedInteractions],
    });
  }

  function onSeoMetadataChange(e, { name: fieldName, value }) {
    updateMenuItemState({ [fieldName]: value });
  }

  function onExtraDataChange(newExtraData) {
    updateMenuItemState({ extraData: newExtraData || {} });
  }

  const { url: pdpUrl } = _head(filterClickthruInteractions(interactions)) || {};
  const areCtasEnabled = areCtasEnabledForRecipe(activePatch);
  const isClickTroughUrlEnabled = isClickTroughUrlEnabledForBusiness(business, activePatch);
  const isFeaturedEnabled = isFeaturedFeatureEnabled(activePatch);
  const isMultipleImagesEnabled = isMultipleImagesSupported(activePatch);
  const shouldShowDetailedDescription = isDetailedDescriptionEnabled(activePatch);
  const settingsOptions = getBusinessMenuSettings(business, SETTINGS_OPTIONS, {
    allowFeaturedSetting: isFeaturedEnabled,
  });

  const itemBusinessFiles = sortItemsByList(businessFiles, files || [], 'id');

  const showSeoMetadataField = isAdvancedSeoSupported(activePatch);

  const showAdvancedMultiLocationFeatures =
    isAdvancedLocationFeaturesSupported(activePatch) && isMultiLocation;

  return (
    <InventoryConsumer>
      {({ copies: { itemDescription } }) => (
        <EditMenuModal
          open={open}
          closeModal={() => {
            if (isNew) {
              onDeleteNewItem();
            }
            closeModal();
          }}
          className="edit-menu-schedule edit-menu-item"
          description={itemDescription}
          isNewItem={isNew}
          newItemTitle="Create Item"
          existingItemTitle="Edit Item"
          content={
            <>
              <h2>
                <span className="red asterisk">Name</span>
                <HelpTooltip
                  title="Item Name"
                  content="Enter the name of the Item that the Customer will order."
                />
              </h2>
              <Input
                name="name"
                defaultValue={name}
                placeholder="e.g. Turkey Club"
                onChange={handleChange}
              />
              <h2>
                {shouldShowDetailedDescription && 'Short '}Description
                <HelpTooltip
                  title={`${shouldShowDetailedDescription ? 'Short' : ''} Description`}
                  content={ITEM_SHORT_DESCRIPTION_TOOL_TIP}
                />
              </h2>
              <div className="item-description-ai-container">
                <CatalogObjectDescriptionInput
                  field={{
                    name: 'description',
                    placeholder: ITEM_SHORT_DESCRIPTION_PLACEHOLDER,
                  }}
                  catalogObject={{
                    name,
                    type: ITEM_CATALOG_OBJECT_TYPE,
                    parentName: category.name,
                    description,
                    prices: itemVariations,
                  }}
                  prompt={{
                    minOutputLength: ITEM_SHORT_DESCRIPTION_MIN_LENGTH,
                    maxOutputLength: ITEM_SHORT_DESCRIPTION_MAX_LENGTH,
                    additionalRequirements: ITEM_SHORT_DESCRIPTION_ADDITIONAL_REQUIREMENTS,
                  }}
                  onChange={handleChange}
                  disableAiAssistant={isNew}
                />
              </div>

              {shouldShowDetailedDescription && (
                <>
                  <h2>
                    Detailed Description
                    <HelpTooltip
                      title="Detailed Description"
                      content={ITEM_LONG_DESCRIPTION_TOOL_TIP}
                    />
                  </h2>
                  <div className="item-description-ai-container">
                    <CatalogObjectDescriptionInput
                      field={{
                        name: 'fullDescription',
                        placeholder: ITEM_LONG_DESCRIPTION_PLACEHOLDER,
                      }}
                      catalogObject={{
                        name,
                        type: ITEM_CATALOG_OBJECT_TYPE,
                        parentName: category.name,
                        description: fullDescription,
                        prices: itemVariations,
                      }}
                      prompt={{
                        minOutputLength: ITEM_LONG_DESCRIPTION_MIN_LENGTH,
                        maxOutputLength: ITEM_LONG_DESCRIPTION_MAX_LENGTH,
                        additionalRequirements: '',
                      }}
                      onChange={handleChange}
                      disableAiAssistant={isNew}
                    />
                  </div>
                </>
              )}
              {areAdditionalServiceAttributesSupported(activePatch) && (
                <>
                  <h2>
                    <span>Service Time (in minutes)</span>
                    <HelpTooltip
                      title="Service Time"
                      content="Enter the average number of minutes it takes to complete this service"
                    />
                  </h2>
                  <Input
                    name="duration"
                    type="number"
                    step={1}
                    defaultValue={duration}
                    onChange={handleChange}
                  />
                </>
              )}
              {isClickTroughUrlEnabled && (
                <ProductDescriptionPageWidget
                  value={pdpUrl}
                  onChange={onUpdateClickthruInteractions}
                />
              )}

              {!isMultipleImagesEnabled && (
                <>
                  <h2>
                    Image
                    <HelpTooltip
                      title="Item Image"
                      content="Optionally add an image for the Item. This will be shown to Customers when ordering."
                    />
                  </h2>
                  <EditFileField url={image} onChange={onImageChange} onDelete={onImageDelete}>
                    <EditFileField.FileInput />
                  </EditFileField>
                  <ImageAltTextField
                    altText={imageAltText}
                    imageUrl={getImageUrl(image)}
                    onChangeAltText={onChangeAltText}
                    title={<Header>Image Alt Text</Header>}
                    fluid
                    disabled={isImageUrlEmpty(image)}
                  />
                </>
              )}

              {isMultipleImagesEnabled && (
                <>
                  <h2>
                    Images
                    <HelpTooltip
                      title="Images"
                      content="Optionally add images for the Item. If multiple images are selected, a carousel will be shown."
                    />
                  </h2>
                  <FileSelection
                    files={itemBusinessFiles}
                    onUpdateFiles={onUpdateFiles}
                    uploadSourceType={SOURCE_TYPE_MENU}
                  />
                </>
              )}

              {areCtasEnabled && (
                <>
                  <h2>
                    Buttons
                    <HelpTooltip title="Buttons" content={BUTTONS_TOOLTIP_DESCRIPTION} />
                  </h2>
                  <WebsiteComponentInteractionContainer
                    interactions={filterNonClickthruInteractions(interactions).map(
                      objectToSnakeCase,
                    )}
                    onUpdate={onUpdateWebsiteComponentInteractions}
                  />
                </>
              )}
              {areAdditionalServiceAttributesSupported(activePatch) && (
                <>
                  <h2>
                    Additional Attributes
                    <HelpTooltip
                      title="Additional Attributes"
                      content="Add custom information, warnings, or instructions for this item."
                    />
                  </h2>
                  <ExtraDataGroupForm
                    extraData={formData.extraData || { attributes: [] }}
                    onChange={onExtraDataChange}
                  />
                </>
              )}

              <h2>
                Pricing
                <HelpTooltip
                  title="Item Price and Variants"
                  content="Set the price for the Item. If you wish to include variations of this item you may do so. Examples of variations are Small, Medium, Large which can all carry different Item pricing. The modifier pricing is the same across all variations."
                />
              </h2>

              {isGlobalPriceLabelSupported(activePatch) && (
                <div className="global-price-label-container">
                  <h3>
                    Global Price Label
                    <HelpTooltip title="Global Price Label" content={GLOBAL_PRICE_LABEL_TOOL_TIP} />
                  </h3>
                  <Input
                    name="globalPriceLabel"
                    defaultValue={globalPriceLabel}
                    placeholder="e.g. $10.00+"
                    onChange={handleChange}
                  />
                </div>
              )}

              <div className="variants-container">
                <h3>Variants</h3>
                <p>
                  Variants are the different types of your item, like &apos;Small&apos; and
                  &apos;Large&apos; and can have different prices
                </p>

                <MenuItemVariants variations={itemVariations || []} onChange={onVariationsChange} />
              </div>
              <h2>
                Modifier Sets
                <HelpTooltip
                  title="Item Modifier Sets"
                  content="Assign or create new Modifier Sets to show the Customer when ordering this Item."
                />
              </h2>
              <MenuItemModifierSets
                itemIndex={itemIndex}
                item={formData}
                onItemModifierSetsChange={onUpdateModifierSets}
              />

              {!isNew && (
                <SettingsWidget data={formData} onChange={handleChange} options={settingsOptions} />
              )}

              {showAdvancedMultiLocationFeatures && (
                <div className="location-settings-container">
                  <h2>Location Settings</h2>
                  <ServiceItemLocationSettings
                    settings={locationSettings.map((setting) => ({
                      ...setting,
                      locationId: setting.location,
                    }))}
                    onChange={onUpdateLocationSettings}
                  />
                </div>
              )}

              {showSeoMetadataField && (
                <>
                  <h2>SEO Information</h2>
                  <SeoMetadataFields
                    onChange={onSeoMetadataChange}
                    pageType={ITEM_PAGE_TYPE}
                    header=""
                  >
                    <SeoMetadataFields.SeoTitle
                      showWarnings
                      title={seoTitle}
                      placeholder={createSeoTitlePlaceholder(name, businessName)}
                      promptClass={ITEM_SEO_TITLE_PROMPT_CLASS}
                      additionalParams={{ itemId }}
                      disableAiAssistant={isNew}
                    />
                    <SeoMetadataFields.SeoDescription
                      showWarnings
                      description={seoDescription}
                      placeholder={description}
                      promptClass={ITEM_SEO_DESCRIPTION_PROMPT_CLASS}
                      additionalParams={{ itemId }}
                      disableAiAssistant={isNew}
                    />
                    <SeoMetadataFields.SeoOgTitle
                      title={seoOgTitle}
                      placeholder={
                        _isEmpty(seoTitle)
                          ? createSeoTitlePlaceholder(name, businessName)
                          : seoTitle
                      }
                    />
                    <SeoMetadataFields.SeoOgDescription
                      description={seoOgDescription}
                      placeholder={seoDescription || description || ''}
                    />
                    <SeoMetadataFields.SeoOgImage image={seoOgImageId} />
                  </SeoMetadataFields>
                </>
              )}
            </>
          }
          actions={
            <>
              {!_isEmpty(errorMessages) &&
                errorMessages.map((errorMessage) => (
                  <div key={errorMessage} className="menu-error-message">
                    {errorMessage}
                  </div>
                ))}
              <Button color={isNew ? null : 'red'} onClick={() => setDeleteConfirmationOpen(true)}>
                {isNew ? 'Cancel' : 'Delete'}
              </Button>
              <Confirm
                dimmer="inverted"
                className="delete-confirmation"
                content={`Are you sure you want to ${isNew ? 'cancel' : 'delete this item'}?`}
                confirmButton={{
                  content: `${isNew ? 'Confirm' : 'Delete'}`,
                  loading: deleteLoading,
                }}
                open={deleteConfirmationOpen}
                onCancel={() => setDeleteConfirmationOpen(false)}
                onConfirm={isNew ? onDeleteNewItem : onDeleteItem}
              />
              <Button
                className="action"
                onClick={isNew ? onSaveNewItem : onSaveItem}
                loading={saveLoading}
                content="Save"
              />
            </>
          }
        />
      )}
    </InventoryConsumer>
  );
}

EditMenuItem.propTypes = propTypes;
EditMenuItem.defaultProps = defaultProps;
